import React, { useCallback, useMemo } from 'react'

import { TagsEventType } from '@aytm/app/insights/dashboard/components/hooks/types'
import { Attribute, Benchmark, Editor, Value } from '@aytm/app/insights/dashboard/components/ui'
import { SectionToggle } from '@aytm/app/insights/dashboard/components/ui/SectionToggle/SectionToggle'
import { CaptionType, EditorType, ItemType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import useAttributes from '../useAttributes'
import { useRow } from '../useRow'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { stateSelector, shouldUpdate, type StateSelectorParams } from './selectors'

type UseDataProps = {
  items: Array<ItemType>
  editor: EditorType
  subjectType: string
  wait?: boolean
  sigtestCount: number
  enableSort: boolean
  enableChart: boolean
  onSortByAttributeChange: (caption: Partial<CaptionType>) => void
  onAttributeDelete: (caption: Partial<CaptionType>) => void
  onAttributeReplace?: (arg: { from: CaptionType; to: Partial<CaptionType> }) => void
  onAttributeChange?: (attribute: string) => void
  onAttributeDrag: (
    arg0: {
      action: 'drop' | 'enter' | 'leave'
      data: Partial<CaptionType> & {
        [key: string]: unknown
        newSection?: string
      }
    },
    arg1?: unknown
  ) => void
  onItemPick: (event: unknown) => void
  onItemPin: (event: unknown) => void
  onItemDrag: (event: unknown) => void
  onItemHover: (event: unknown) => void
  onPopup: (event?: unknown) => void
  onTagsUpdate: (event: TagsEventType) => void
  onShowSectionChange: (caption: unknown) => void
  onEditorChange: (editor: unknown) => void
  onMetricsChange: {
    (item: unknown): void
    (item: unknown, metric: string): void
  }
  draggableItems: boolean
  dragItemIndex: number
  dragAttribute: { attribute: string }
}

type UseDataResult = {
  rows: React.ReactNode[][] // Rows of the table
  spanRowIndex: number // Index of the row to be spanned
  extraColumns: number // Number of extra columns
}

const useData = (props: UseDataProps): UseDataResult => {
  const {
    subjects,
    attributes,
    sortByAttribute,
    showSigtest,
    sortByMethod,
    currentSplit,
    sortOrder,
    editorMode,
    chartAttribute
  } = usePool<typeof InsightsDashboardPool, StateSelectorParams, InsightsDashboardPoolState>(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  /** Get attributes */
  const { getCaptions, chartMethod } = useAttributes()

  const allCaptions = useMemo(() => getCaptions(), [getCaptions])

  const spanRowIndex = -1

  // Local properties

  /** Local items */
  const items = useMemo(() => props.items || [], [props.items])

  /** Local editor */
  const editor = useMemo(() => props.editor || null, [props.editor])

  /** Universal row creation function */
  const { getRow, extraColumns } = useRow({ items, editor })

  const getBenchmarkRow = useCallback(
    (caption: CaptionType) =>
      getRow(
        ({ key }) => (
          <SectionToggle
            key={key}
            target={caption.target}
            caption={caption.section}
            show={!caption.collapsed}
            onExpandChange={props.onShowSectionChange}
            onDrag={props.onAttributeDrag}
          />
        ),
        ({ key, isBenchmark }) =>
          isBenchmark ? <Editor key={key} editor={editor} rowType={'title'} text={caption.section} /> : null,
        ({ key, itemIndex, item, isExtraColumn, isEditing }) => {
          const controls = ['pin']
          return isExtraColumn ? (
            isEditing ? (
              <div key={key} />
            ) : (
              <Editor
                rowType={'control'}
                editor={editor}
                itemIndex={itemIndex}
                item={item}
                subjectType={props.subjectType}
                onEditorChange={props.onEditorChange}
              />
            )
          ) : (
            <Benchmark
              key={key}
              item={item}
              index={itemIndex}
              draggable={props.draggableItems}
              dragItemIndex={props.dragItemIndex}
              editor={editor}
              controls={controls}
              onPin={props.onItemPin}
              onDrag={props.onItemDrag}
            />
          )
        }
      ),
    [
      editor,
      getRow,
      props.dragItemIndex,
      props.draggableItems,
      props.onEditorChange,
      props.onItemDrag,
      props.onItemPin,
      props.onShowSectionChange,
      props.subjectType,
      props.onAttributeDrag
    ]
  )

  /** Create attribute row */
  const getAttributeRow = useCallback(
    (caption: CaptionType, index: number) => {
      const attribute = caption.attribute
      const type = attributes[attribute]?.type
      return getRow(
        // Captions
        ({ key }) =>
          caption ? (
            <Attribute
              key={`${attribute}-${key}-attribute-${sortByAttribute}`}
              caption={caption}
              currentSplit={currentSplit}
              drag={attribute === props.dragAttribute?.attribute}
              // enableChart={props.enableChart}
              enableSort={props.enableSort}
              onClick={props.onSortByAttributeChange}
              onDelete={props.onAttributeDelete}
              onDrag={props.onAttributeDrag}
              onMetricsChange={props.onMetricsChange}
              onReplace={props.onAttributeReplace}
              sortByAttribute={sortByAttribute}
              sortByMethod={sortByMethod}
              chartAttribute={chartAttribute}
              chartMethod={chartMethod}
              sortOrder={sortOrder}
              subjectType={props.subjectType}
            />
          ) : (
            <Attribute.Placeholder key={key} />
          ),
        // Editor
        ({ key, isBenchmark }) =>
          isBenchmark && ['percent', 'number', 'date'].includes(type) ? (
            <Editor
              key={`${attribute}-${key}-editor`}
              rowType={'tags' === type ? '' : 'data'}
              editor={editor}
              caption={caption}
              subjects={subjects}
              attributeIndex={index}
              subjectType={props.subjectType}
              onEditorChange={props.onEditorChange}
            />
          ) : 'tags' === type && 'add' === editor.type && -1 === editor.itemIndex ? (
            <div key={key} />
          ) : null,
        // Cell
        ({ key, itemIndex, isExtraColumn }) =>
          isExtraColumn ? (
            <div key={key} />
          ) : (
            <Value
              key={`${attribute}-${key}-value`}
              items={items}
              editor={editor}
              index={itemIndex}
              caption={caption}
              wait={Boolean(props.wait)}
              sigtestCount={props.sigtestCount}
              showSigtest={showSigtest}
              editorMode={editorMode}
              subjectType={props.subjectType}
              onItemPick={props.onItemPick}
              onPopup={props.onPopup}
              onTagsUpdate={props.onTagsUpdate}
            />
          )
      )
    },
    [
      attributes,
      chartAttribute,
      currentSplit,
      editor,
      editorMode,
      getRow,
      items,
      props.dragAttribute?.attribute,
      props.enableSort,
      props.onAttributeDelete,
      props.onAttributeDrag,
      props.onAttributeReplace,
      props.onEditorChange,
      props.onItemPick,
      props.onMetricsChange,
      props.onPopup,
      props.onSortByAttributeChange,
      props.onTagsUpdate,
      props.sigtestCount,
      props.subjectType,
      props.wait,
      showSigtest,
      chartMethod,
      sortByAttribute,
      sortByMethod,
      sortOrder,
      subjects
    ]
  )

  /** Create metadata row */
  const getSectionRow = useCallback(
    (caption: CaptionType) =>
      getRow(
        ({ key }) => (
          <SectionToggle
            key={key}
            target={caption.target}
            caption={caption.section}
            show={!caption.collapsed}
            onExpandChange={props.onShowSectionChange}
            onDrag={props.onAttributeDrag}
          />
        ),
        ({ key, isBenchmark }) =>
          isBenchmark ? <Editor key={key} editor={editor} rowType={'title'} text={caption.section} /> : null,
        ({ key }) => <div key={key} />
      ),
    [editor, getRow, props.onShowSectionChange, props.onAttributeDrag]
  )

  /** Create editor row */
  const getEditorRow = useCallback(
    () =>
      getRow(
        ({ key }) => <div key={key} />,
        ({ key }) => (
          <Editor
            key={key}
            rowType={'control'}
            wait={props.wait}
            editor={editor}
            subjectType={props.subjectType}
            onEditorChange={props.onEditorChange}
          />
        ),
        ({ key }) => <div key={key} />
      ),
    [editor, getRow, props.onEditorChange, props.subjectType, props.wait]
  )

  /** Create caption rows */
  const rows = useMemo(() => {
    // Result rows
    const rows = []
    // Create attribute and control rows
    let captionIndex = 0
    for (const [index, caption] of allCaptions.entries()) {
      if ('section' === caption.type) {
        if (0 === index) {
          rows.push(getBenchmarkRow(caption as CaptionType))
        } else {
          rows.push(getSectionRow(caption as CaptionType))
        }
      } else {
        rows.push(getAttributeRow(caption as CaptionType, captionIndex++))
      }
    }
    // Push Editor controls (save, delete, confirm buttons) row if needed
    if (editor && ['edit', 'change'].includes(editor.type)) {
      rows.push(getEditorRow())
    }
    // Return result
    return rows
  }, [editor, allCaptions, getAttributeRow, getSectionRow, getBenchmarkRow, getEditorRow])

  // Return result
  return { rows, spanRowIndex, extraColumns }
}

export default useData
